
import type {
    DOMConversionMap,
    DOMConversionOutput,
    DOMExportOutput,
    EditorConfig,
    ElementFormatType,
    LexicalEditor,
    LexicalNode,
    NodeKey,
    Spread
} from "lexical";

import { BlockWithAlignableContents } from "@lexical/react/LexicalBlockWithAlignableContents";
import {
    DecoratorBlockNode,
    SerializedDecoratorBlockNode
} from "@lexical/react/LexicalDecoratorBlockNode";
import * as React from "react";

type VimeoComponentProps = Readonly<{
  className: Readonly<{
    base: string;
    focus: string;
  }>;
  format: ElementFormatType | null;
  nodeKey: NodeKey;
  videoID: string;
}>;

function getVimeoParts(videoID: string): { hash: string | null; videoId: string; } {
    const [videoId, hashQuery] = videoID.split("?h=");

    return {
        hash: hashQuery || null,
        videoId
    };
}

function getVimeoEmbedUrl(videoID: string): string {
    const { hash, videoId } = getVimeoParts(videoID);

    if (hash) {
        return `https://player.vimeo.com/video/${videoId}?h=${hash}`;
    }

    return `https://player.vimeo.com/video/${videoId}/`;
}

function getVimeoShareUrl(videoID: string): string {
    const { hash, videoId } = getVimeoParts(videoID);

    if (hash) {
        return `https://vimeo.com/${videoId}/${hash}`;
    }

    return `https://vimeo.com/${videoId}`;
}

function VimeoComponent({
    className,
    format,
    nodeKey,
    videoID
}: VimeoComponentProps) {
    return (
        <BlockWithAlignableContents
            className={className}
            format={format}
            nodeKey={nodeKey}
        >
            <iframe
                allowFullScreen
                allow="autoplay; fullscreen; picture-in-picture"
                frameBorder="0"
                height="315"
                src={getVimeoEmbedUrl(videoID)}
                title="Vimeo video"
                width="560"
            />
        </BlockWithAlignableContents>
    );
}

export type SerializedVimeoNode = Spread<
  {
    videoID: string;
  },
  SerializedDecoratorBlockNode
>;

function $convertVimeoElement(
    domNode: HTMLElement
): null | DOMConversionOutput {
    const videoID = domNode.getAttribute("data-lexical-vimeo");
    if (videoID) {
        const node = $createVimeoNode(videoID);
        return { node };
    }
    return null;
}

export class VimeoNode extends DecoratorBlockNode {
  public __id: string;

  public static getType(): string {
      return "vimeo";
  }

  public static clone(node: VimeoNode): VimeoNode {
      return new VimeoNode(node.__id, node.__format, node.__key);
  }

  public static importJSON(serializedNode: SerializedVimeoNode): VimeoNode {
      const node = $createVimeoNode(serializedNode.videoID);
      node.setFormat(serializedNode.format);
      return node;
  }

  public exportJSON(): SerializedVimeoNode {
      return {
          ...super.exportJSON(),
          type: "vimeo",
          version: 1,
          videoID: this.__id
      };
  }

  public constructor(id: string, format?: ElementFormatType, key?: NodeKey) {
      super(format, key);
      this.__id = id;
  }

  public exportDOM(): DOMExportOutput {
      const element = document.createElement("iframe");
      element.setAttribute("data-lexical-vimeo", this.__id);
      element.setAttribute("width", "560");
      element.setAttribute("height", "315");
      element.setAttribute(
          "src",
          getVimeoEmbedUrl(this.__id)
      );
      element.setAttribute("frameborder", "0");
      element.setAttribute(
          "allow",
          "autoplay; fullscreen; picture-in-picture"
      );
      element.setAttribute("allowfullscreen", "true");
      element.setAttribute("title", "Vimeo video");
      return { element };
  }

  public static importDOM(): DOMConversionMap | null {
      return {
          iframe: (domNode: HTMLElement) => {
              if (!domNode.hasAttribute("data-lexical-vimeo")) {
                  return null;
              }
              return {
                  conversion: $convertVimeoElement,
                  priority: 1
              };
          }
      };
  }

  public updateDOM(): false {
      return false;
  }

  public getId(): string {
      return this.__id;
  }

  public getTextContent(): string {
      return getVimeoShareUrl(this.__id);
  }

  public decorate(_editor: LexicalEditor, config: EditorConfig): JSX.Element {
      const embedBlockTheme = config.theme.embedBlock || {};
      const className = {
          base: embedBlockTheme.base || "",
          focus: embedBlockTheme.focus || ""
      };
      return (
          <VimeoComponent
              className={className}
              format={this.__format}
              nodeKey={this.getKey()}
              videoID={this.__id}
          />
      );
  }
}

export function $createVimeoNode(videoID: string): VimeoNode {
    return new VimeoNode(videoID);
}

export function $isVimeoNode(
    node: VimeoNode | LexicalNode | null | undefined
): node is VimeoNode {
    return node instanceof VimeoNode;
}
